import type {ItemIdentifier} from '@github-ui/issue-viewer/Types'
import {SingleSignOnBanner} from '@github-ui/single-sign-on-banner'
import {useSso} from '@github-ui/use-sso'
import {useRef} from 'react'
import {graphql, useFragment} from 'react-relay'

import {useActiveSearchQuery, useQueryEditState} from '../../contexts/QueryContext'
import {useIssuesDashboardSemanticSearchFeaturePreview} from '../../hooks/use-issues-dashboard-semantic-search-feature-preview'
import {useIssuesIndexSemanticSearchToggleFeaturePreview} from '../../hooks/use-issues-index-semantic-search-toggle-feature-preview'
import type {LoadSearchQuery} from '../../pages/shared'
import NewViewExperience from '../list/NewViewExperience'
import type {SearchCurrentViewFragment$key} from './__generated__/SearchCurrentViewFragment.graphql'
import type {SearchRepositoryFragment$key} from './__generated__/SearchRepositoryFragment.graphql'
import type {SearchRootFragment$key} from './__generated__/SearchRootFragment.graphql'
import styles from './Search.module.css'
import {SearchBar} from './SearchBar'
import {SearchList} from './SearchList'
import {SemanticSearchFeaturePreview} from './SemanticSearchFeaturePreview'

type SearchProps = {
  itemIdentifier: ItemIdentifier | undefined
  currentViewKey: SearchCurrentViewFragment$key
  currentRepository: SearchRepositoryFragment$key | null
  search: SearchRootFragment$key
  loadSearchQuery?: LoadSearchQuery
  showSsoBanner?: boolean
  queryFromCustomView?: string | null
  onSidePanelNavigate?: (issue: ItemIdentifier) => void
  trackIssueNavigation: (position?: number) => void
  updateIssueSearchType?: (issueSearchType: string | null | undefined) => void
  onListItemsChange?: (items: ItemIdentifier[]) => void
}

export function Search({
  itemIdentifier,
  currentViewKey,
  currentRepository,
  search,
  loadSearchQuery,
  queryFromCustomView,
  onSidePanelNavigate,
  showSsoBanner,
  trackIssueNavigation,
  updateIssueSearchType,
  onListItemsChange,
}: SearchProps) {
  const {ssoOrgs} = useSso()
  const orgNames = ssoOrgs.map(o => o['login']).filter(n => n !== undefined)

  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment SearchRootFragment on Searchable
      @argumentDefinitions(
        query: {type: "String!"}
        first: {type: "Int"}
        labelPageSize: {type: "Int!"}
        skip: {type: "Int", defaultValue: null}
        fetchRepository: {type: "Boolean!"}
      ) {
        ...SearchList
          @arguments(
            query: $query
            first: $first
            labelPageSize: $labelPageSize
            fetchRepository: $fetchRepository
            skip: $skip
          )
      }
    `,
    search,
  )

  const repoData = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment SearchRepositoryFragment on Repository {
        ...SearchListRepo
        ...SearchBarRepo
      }
    `,
    currentRepository,
  )

  const currentViewData = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment SearchCurrentViewFragment on Shortcutable {
        ...SearchBarCurrentViewFragment
      }
    `,
    currentViewKey,
  )

  const activeSearchQuery = useActiveSearchQuery()
  const {isNewView} = useQueryEditState()

  const indexFeaturePreview = useIssuesIndexSemanticSearchToggleFeaturePreview()
  const dashboardFeaturePreview = useIssuesDashboardSemanticSearchFeaturePreview()
  const semanticSearchFeaturePreview = currentRepository ? indexFeaturePreview : dashboardFeaturePreview

  const listRef = useRef<HTMLUListElement | undefined>(undefined)

  return (
    <div className={styles.SearchContainer}>
      <SearchBar
        currentViewKey={currentViewData}
        currentRepository={repoData ?? null}
        queryFromCustomView={queryFromCustomView}
      />
      {isNewView && !activeSearchQuery ? (
        <NewViewExperience />
      ) : (
        <>
          {showSsoBanner && <SingleSignOnBanner protectedOrgs={orgNames} />}
          <SearchList
            itemIdentifier={itemIdentifier}
            search={data}
            repository={repoData ?? null}
            loadSearchQuery={loadSearchQuery}
            query={activeSearchQuery}
            queryFromCustomView={queryFromCustomView}
            listRef={listRef}
            onSidePanelNavigate={onSidePanelNavigate}
            onIssueNavigate={trackIssueNavigation}
            updateIssueSearchType={updateIssueSearchType}
            onListItemsChange={onListItemsChange}
          />
          <SemanticSearchFeaturePreview {...semanticSearchFeaturePreview} />
        </>
      )}
    </div>
  )
}
